/**
 * A trace of how one hit got to its number.
 *
 * The shape is the mod's own damage log, `DamageEvent.getInfoHoverMessage`
 * (DamageEvent.java:567-646), which players turn on with the `damage_messages` config and read
 * as a hover on the combat message:
 *
 *     Spell: Fireball
 *     Fire:
 *     Base Damage: 1240
 *     Damage Info:
 *       [Source]: Flat Damage: +182
 *       [Source]: Additive Damage: x2.35
 *       [Target]: Elemental Mitigation: x0.25
 *     Multipliers:
 *       Stat: More Fire Damage: x1.20
 *     Final Damage: 1092
 *     - Bonus Damage Types:
 *       ... the same block again, per element ...
 *     Total Combined Damage: 1310
 *
 * Two deliberate departures from it.
 *
 * **It records who wrote into each layer.** The game's `StatLayerData` is a bare accumulator —
 * every stat that increases damage adds into one `additive_damage` float and the log prints
 * only the total, so "x2.35" never says which +35% came from where. `MoreMultiData` is the one
 * exception, and it is the exception precisely because a MORE has to be applied separately.
 * Recording the writes costs one array push per stat per event and turns the whole log into
 * something you can drill into, which is the entire point of a planner.
 *
 * **It records the before and after of the number itself**, so a row can show what a layer did
 * rather than only what it was set to.
 *
 * A trace is only built when one is asked for. `simulateHit` runs the pipeline twice, once per
 * crit branch, and a bonus element is a whole nested event — so the recorder is opt-in and the
 * default path allocates nothing.
 */
/**
 * The mutable side of a trace while an event is in flight.
 *
 * `statId` is set by the sweep around each queued effect, so a layer write knows who made it
 * without every write site having to pass it down.
 */
export class Recorder {
    statId;
    effectId;
    /**
     * Which half of the event the running stat belongs to, set by the sweep alongside `statId`.
     *
     * Not the layer's side. The game keeps one accumulator per layer for both halves and labels it
     * after whichever wrote first, so the mob's `dmg_received` lands in a `[Source]` additive
     * layer. Stamping the contribution with that label sent the panel looking for the stat on
     * your sheet, where nothing grants it.
     */
    side;
    contributions = [];
    /** The accumulator label each contribution was written into, parallel to `contributions`. */
    layerSides = [];
    write(layerId, numberId, side, kind, value, before, after, element) {
        // A write that changes nothing is noise — the game drops no-op MORE multipliers for the
        // same reason (`addMoreMulti` skips `multi == 1`).
        if (value === 0)
            return;
        this.contributions.push({
            statId: this.statId ?? "(unattributed)",
            effectId: this.effectId,
            layerId,
            numberId,
            side: this.side ?? side,
            element,
            kind,
            value,
            before,
            after,
        });
        this.layerSides.push(side);
    }
    /**
     * Attaches the arithmetic behind the write that just happened.
     *
     * Separate from `write` because the accumulator cannot know it: `reduce` is handed one
     * finished number, and the sheet value, the penetration and the clamp that produced it all
     * live inside the effect.
     *
     * `since` is {@link contributions}' length taken *before* the write, and it is what makes
     * "there may be nothing to annotate" safe. A write of zero is dropped — which is right, a
     * mitigation that changed nothing has nothing to explain — and without the guard the detail
     * then landed on whichever contribution happened to be last. On a physical hit that was
     * literally happening: `resistEffect` reduces by 0 against a mob with no physical resistance,
     * and its `{sheet: 0, cap: 75}` was attaching to the `phys_to_water` conversion row directly
     * above it, so the panel offered a resistance drill-down under a conversion.
     */
    annotateSince(since, detail) {
        if (this.contributions.length === since)
            return;
        const last = this.contributions.at(-1);
        if (last !== undefined)
            last.mitigation = detail;
    }
    /**
     * Attaches the multiplication the event did to a value on its way into a layer.
     *
     * `since` is {@link contributions}' length taken *before* the write, and the guard it gives is
     * not defensive padding: `addBonusEleDmg` only reaches a layer when the flat damage is the
     * element the hit already deals and spawns a bonus-element event otherwise, so without the
     * check an off-element write would annotate whichever contribution happened to be last — a
     * different stat, on a different layer, with a multiplier that was never applied to it.
     */
    scaledSince(since, raw, multi) {
        if (multi === 1 || this.contributions.length === since)
            return;
        const last = this.contributions.at(-1);
        if (last !== undefined)
            last.scaled = { raw, multi, reason: "effectiveness" };
    }
    /**
     * The writes made into one layer accumulator, in the order they happened. `element`
     * distinguishes the per-target-element accumulators `getConversionLayer` creates.
     */
    forLayer(layerId, numberId, side, element) {
        return this.contributions.filter((c, i) => c.layerId === layerId &&
            c.numberId === numberId &&
            this.layerSides[i] === side &&
            (element === undefined || c.element === element));
    }
}
/** Sums a trace tree's `finalNumber` — the game's `Total Combined Damage`. */
export function traceTotal(trace) {
    let total = trace.finalNumber;
    for (const child of trace.children)
        total += traceTotal(child);
    return total;
}
//# sourceMappingURL=breakdown.js.map